import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
    Calendar, Cloud, LayoutGrid, CircleCheckBig, Mail, MessagesSquare, Settings, Users, type LucideIcon
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useFolders, useTasks } from "@/api/hooks";
import { MESSAGE_DRAG_TYPE, readMessageDrag, useCreateTaskFromMessage } from "@/features/tasks/fromMessage";
import { findApp, suiteApps, type SuiteAppId } from "./suiteApps";

interface NavItem {
    to: string;
    label: string;
    icon: LucideIcon;
}

const items: NavItem[] = [
    { to: "/mail", label: "Mail", icon: Mail },
    { to: "/calendar", label: "Calendar", icon: Calendar },
    { to: "/people", label: "People", icon: Users },
    { to: "/tasks", label: "Tasks", icon: CircleCheckBig }
];

/** Other suite apps, opened in a new tab (URLs from suiteApps.ts). */
const suiteLinks: Array<{ id: SuiteAppId; icon: LucideIcon }> = [
    { id: "connect", icon: MessagesSquare },
    { id: "drive", icon: Cloud }
];

const tile = "relative flex h-10 w-10 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const idle = "text-[var(--mail-text-2)] hover:bg-[var(--mail-hover)] hover:text-foreground";
const active = "bg-[var(--mail-primary-soft)] text-primary";

/** The 3 px accent bar on the active tile's left edge (mock-up rail). */
function ActiveBar() {
    return <span aria-hidden className="absolute -left-1 bottom-2.5 top-2.5 w-[3px] rounded bg-primary" />;
}

function CountBadge({ count }: { count: number }) {
    return (
        <span
            aria-hidden
            className="absolute right-[3px] top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums leading-none text-primary-foreground"
        >
            {count > 99 ? "99+" : count}
        </span>
    );
}

/**
 * NavLink's `className` / `children` render functions can't be used here: the tooltip trigger (`asChild`, a Radix Slot)
 * merges class names as strings, so a function className became the text "({ isActive }) => …" and every tile lost its
 * size and active styling — the rail's icons collapsed together (Connect's rail, which uses plain strings, was fine).
 */
function isActivePath(pathname: string, to: string) {
    return pathname === to || pathname.startsWith(`${to}/`);
}

export function LeftRail() {
    const { pathname } = useLocation();
    // Inbox unread count for the badge on the Mail tile (shares MailPage's polled folder query).
    const folders = useFolders();
    const unread = folders.data?.find((f) => f.kind === "inbox")?.unreadCount ?? 0;
    // Open-tasks count for the badge on the Tasks tile.
    const openTasks = useTasks(false);
    const openCount = openTasks.data?.length ?? 0;
    // Outlook: drop a message on the To Do icon to make it a task.
    const createFromMessage = useCreateTaskFromMessage();
    const [dropHover, setDropHover] = useState(false);

    // "More apps": every live suite app not already on the rail.
    const railIds = new Set<SuiteAppId>(["mail", ...suiteLinks.map((l) => l.id)]);
    const moreApps = suiteApps.filter((a) => !a.comingSoon && !railIds.has(a.id));

    return (
        <nav
            className="flex w-12 shrink-0 flex-col items-center gap-1 border-r border-[var(--mail-line)] bg-[var(--mail-pane)] pb-2 pt-2"
            aria-label="Primary"
        >
            {items.map((item) => {
                const isTasks = item.to === "/tasks";
                const isMail = item.to === "/mail";
                const count = isTasks ? openCount : isMail ? unread : 0;
                const countLabel = isTasks ? `${count} open` : `${count} unread`;
                const isActive = isActivePath(pathname, item.to);
                return (
                    <Tooltip key={item.to}>
                        <TooltipTrigger asChild>
                            <NavLink
                                to={item.to}
                                onDragOver={isTasks ? (e) => {
                                    if (!e.dataTransfer.types.includes(MESSAGE_DRAG_TYPE)) return;
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = "copy";
                                    setDropHover(true);
                                } : undefined}
                                onDragLeave={isTasks ? () => setDropHover(false) : undefined}
                                onDrop={isTasks ? (e) => {
                                    setDropHover(false);
                                    const refs = readMessageDrag(e.dataTransfer);
                                    if (!refs) return;
                                    e.preventDefault();
                                    createFromMessage(refs, "tasks");
                                } : undefined}
                                className={cn(tile, isActive ? active : idle, isTasks && dropHover && "ring-2 ring-primary")}
                                aria-current={isActive ? "page" : undefined}
                                aria-label={count > 0 ? `${item.label} (${countLabel})` : item.label}
                            >
                                {isActive ? <ActiveBar /> : null}
                                <item.icon className="h-5 w-5" aria-hidden />
                                {count > 0 ? <CountBadge count={count} /> : null}
                            </NavLink>
                        </TooltipTrigger>
                        <TooltipContent side="right">{isTasks ? "Tasks — drop a message to create a task" : item.label}</TooltipContent>
                    </Tooltip>
                );
            })}

            <div role="separator" aria-hidden className="my-1 h-px w-6 bg-[var(--mail-line)]" />

            {suiteLinks.map(({ id, icon: Icon }) => {
                const app = findApp(id);
                if (!app || app.comingSoon) return null;
                return (
                    <Tooltip key={id}>
                        <TooltipTrigger asChild>
                            <a
                                href={app.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Open ${app.name} in a new tab`}
                                className={cn(tile, idle)}
                            >
                                <Icon className="h-5 w-5" aria-hidden />
                            </a>
                        </TooltipTrigger>
                        <TooltipContent side="right">{app.name} — {app.blurb}</TooltipContent>
                    </Tooltip>
                );
            })}

            {moreApps.length > 0 ? (
                <DropdownMenu>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <DropdownMenuTrigger aria-label="More apps" className={cn(tile, idle, "data-[state=open]:bg-[var(--mail-hover)]")}>
                                <LayoutGrid className="h-5 w-5" aria-hidden />
                            </DropdownMenuTrigger>
                        </TooltipTrigger>
                        <TooltipContent side="right">More apps</TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent side="right" align="start" className="w-60">
                        <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">Athavita apps</DropdownMenuLabel>
                        {moreApps.map((app) => (
                            <DropdownMenuItem key={app.id} asChild>
                                <a href={app.url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${app.name} in a new tab`}>
                                    <span className="flex min-w-0 flex-col">
                                        <span className="truncate text-sm">{app.name}</span>
                                        <span className="truncate text-[11px] text-muted-foreground">{app.blurb}</span>
                                    </span>
                                </a>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : null}

            <div className="mt-auto" />
            <Tooltip>
                <TooltipTrigger asChild>
                    <NavLink
                        to="/settings"
                        className={cn(tile, isActivePath(pathname, "/settings") ? active : idle)}
                        aria-current={isActivePath(pathname, "/settings") ? "page" : undefined}
                        aria-label="Settings"
                    >
                        {isActivePath(pathname, "/settings") ? <ActiveBar /> : null}
                        <Settings className="h-5 w-5" aria-hidden />
                    </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right">Settings</TooltipContent>
            </Tooltip>
        </nav>
    );
}
